import { useRef, useState, type ChangeEvent } from "react";

import { cn } from "../lib/cn";
import { MinusIcon, PlusIcon } from "../lib/icons";
import { Input, type InputProps } from "./input";

export interface NumberInputProps extends Omit<InputProps, "type" | "trailing" | "value"> {
  value?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  onValueChange?: (value: number) => void;
  /** Suffixe affiché dans le champ : unité, devise, pourcentage. */
  unit?: string;
}

/**
 * Champ numérique avec incrément.
 *
 * Les flèches natives de `<input type="number">` sont masquées : elles mesurent
 * quelques pixels, n'existent pas sur mobile et diffèrent d'un navigateur à
 * l'autre. On les remplace par deux boutons à la bonne taille de cible tactile.
 *
 * Le champ reste un `type="number"` : on conserve le clavier numérique sur
 * mobile et la validation native du formulaire.
 */
export function NumberInput({
  className,
  value,
  defaultValue = 0,
  min,
  max,
  step = 1,
  onValueChange,
  onChange,
  unit,
  disabled,
  ...props
}: NumberInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internal, setInternal] = useState(defaultValue);

  const isControlled = value !== undefined;
  const current = isControlled ? value : internal;

  const commit = (next: number) => {
    // On borne avant de propager : sinon un clic de trop enverrait une valeur
    // hors intervalle que le formulaire refuserait silencieusement.
    const clamped = Math.min(Math.max(next, min ?? -Infinity), max ?? Infinity);
    if (!isControlled) setInternal(clamped);
    onValueChange?.(clamped);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const parsed = Number(event.target.value);
    if (!Number.isNaN(parsed)) commit(parsed);
    onChange?.(event);
  };

  const atMin = min !== undefined && current <= min;
  const atMax = max !== undefined && current >= max;

  const stepButton =
    "flex size-6 items-center justify-center rounded-sm text-fg-muted transition-colors duration-fast " +
    "hover:bg-surface-raised hover:text-fg disabled:pointer-events-none disabled:opacity-30 " +
    "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring";

  return (
    <Input
      ref={inputRef}
      type="number"
      inputMode="decimal"
      value={current}
      onChange={handleChange}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      className={cn("pr-20 tabular-nums", unit && "pr-28", className)}
      trailing={
        <span className="flex items-center gap-1">
          {unit && (
            <span className="mr-1 text-xs text-fg-muted" aria-hidden="true">
              {unit}
            </span>
          )}
          <button
            type="button"
            tabIndex={-1}
            aria-label="Diminuer"
            disabled={disabled || atMin}
            onClick={() => commit(current - step)}
            className={stepButton}
          >
            <MinusIcon className="size-3.5" />
          </button>
          <button
            type="button"
            tabIndex={-1}
            aria-label="Augmenter"
            disabled={disabled || atMax}
            onClick={() => commit(current + step)}
            className={stepButton}
          >
            <PlusIcon className="size-3.5" />
          </button>
        </span>
      }
      {...props}
    />
  );
}
